import React from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { getDocument } from '@atlaskit/browser-apis';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { calculateScrollDepth } from '../utils/analytics';

interface ScrollDepthConfig {
	contentARI: string;
	depthThreshold?: number; // Fire event when depth increases by this amount (default: 0.1 = 10%)
	debounceDelay?: number; // Debounce delay in ms (default: 250)
	trackOnUnload?: boolean; // Fire final event on page unload (default: true)
}

/**
 * Hook for tracking scroll depth analytics on a page.
 * Fires scroll depth events when users scroll through content.
 *
 * Usage:
 * ```
 * const MyComponent = ({ contentARI }) => {
 *   useScrollDepthAnalytics({ contentARI });
 *   return <div>...</div>;
 * };
 * ```
 *
 * @param config - Configuration object
 * @param config.contentARI - The Content ARI identifier for the page
 * @param config.depthThreshold - Only fire event when depth increases by this amount (default: 0.1)
 * @param config.debounceDelay - Debounce scroll events by this many ms (default: 250)
 * @param config.trackOnUnload - Fire final scroll depth event on page unload (default: true)
 */
export function useScrollDepthAnalytics({
	contentARI,
	depthThreshold = 0.02,
	debounceDelay = 100,
	trackOnUnload = true,
}: ScrollDepthConfig): void {
	const maxDepthRef = React.useRef(0);
	const hasTrackedRef = React.useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	React.useEffect(() => {
		if (!contentARI) {
			return;
		}

		const document = getDocument();
		if (!document) {
			return;
		}

		// find scrollable container i.e. #main-content on design-system-docs
		let scrollableElement: Window | HTMLElement = window;
		const mainContent = document.getElementById('main-content');
		if (mainContent) {
			const style = window.getComputedStyle(mainContent);
			if (
				(style.overflowY === 'auto' || style.overflowY === 'scroll') &&
				mainContent.scrollHeight > mainContent.clientHeight
			) {
				scrollableElement = mainContent;
			}
		}

		// init maxDepthRef with current scroll depth
		let initialDepth = 0;
		if (scrollableElement instanceof HTMLElement) {
			initialDepth =
				scrollableElement.scrollTop /
				(scrollableElement.scrollHeight - scrollableElement.clientHeight);
			initialDepth = Math.min(Math.max(initialDepth, 0), 1);
		} else {
			initialDepth = calculateScrollDepth();
		}
		maxDepthRef.current = initialDepth;

		// debouncing
		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		const debouncedScroll = () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			timeoutId = setTimeout(() => {
				// calculate depth based on scrollable element
				let currentDepth = 0;
				if (scrollableElement instanceof HTMLElement) {
					const depth =
						scrollableElement.scrollTop /
						(scrollableElement.scrollHeight - scrollableElement.clientHeight);
					currentDepth = Math.min(Math.max(depth, 0), 1);
				} else {
					currentDepth = calculateScrollDepth();
				}
				const previousDepth = maxDepthRef.current;

				// only fire event if depth increased by threshold or near bottom
				if (currentDepth > previousDepth + depthThreshold || currentDepth >= 0.95) {
					maxDepthRef.current = currentDepth;
					const analyticsEvent = createAnalyticsEvent({
						action: 'scrolled',
						actionSubject: 'page',
					});
					analyticsEvent.context = analyticsEvent.context || [];
					analyticsEvent.context.push({
						source: 'scrollDepth',
					});
					fireUIAnalytics(analyticsEvent, 'scrollDepth', {
						contentARI,
						scrollDepth: Math.round(currentDepth * 100),
					});
					hasTrackedRef.current = true;
				}
			}, debounceDelay);
		};

		// handle visibility changes - pause tracking when tab is hidden
		const handleVisibilityChange = () => {
			if (document.hidden) {
				// tab is hidden, remove scroll listener to save resources
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
				window.removeEventListener('scroll', debouncedScroll);
			} else {
				// tab is visible again, re-attach scroll listener
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
				window.addEventListener('scroll', debouncedScroll, { passive: true });
			}
		};

		const handleBeforeUnload = () => {
			if (trackOnUnload && hasTrackedRef.current) {
				// fire final scroll depth event before leaving
				const finalEvent = createAnalyticsEvent({
					action: 'scrolled',
					actionSubject: 'page',
				});
				finalEvent.context = finalEvent.context || [];
				finalEvent.context.push({
					source: 'scrollDepthFinal',
				});
				fireUIAnalytics(finalEvent, 'scrollDepthFinal', {
					contentARI,
					scrollDepth: Math.round(maxDepthRef.current * 100),
					isFinal: true,
				});
			}
		};

		if (scrollableElement instanceof HTMLElement) {
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			scrollableElement.addEventListener('scroll', debouncedScroll, { passive: true });
		} else {
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			window.addEventListener('scroll', debouncedScroll, { passive: true });
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			document.addEventListener('scroll', debouncedScroll, { passive: true });
		}

		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		document.addEventListener('visibilitychange', handleVisibilityChange);

		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		window.addEventListener('beforeunload', handleBeforeUnload);

		// cleanup
		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (scrollableElement instanceof HTMLElement) {
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
				scrollableElement.removeEventListener('scroll', debouncedScroll);
			} else {
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
				window.removeEventListener('scroll', debouncedScroll);
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
				document.removeEventListener('scroll', debouncedScroll);
			}
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [contentARI, depthThreshold, debounceDelay, trackOnUnload, createAnalyticsEvent]);
}
